import React, { useEffect, useState } from "react";
import {
  Spin,
  Typography,
  Card,
  Row,
  Col,
  Tag,
  Button,
  Form,
  DatePicker,
  Input,
  Modal,
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import LeadService from "../../services/LeadService";
import EarningDetailsDialog from "./EarningDetailsDialog";

import "./style.css";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const MyLeads = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState(dayjs().subtract(30, "day"));
  const [toDate, setToDate] = useState(dayjs());
  const [searchText, setSearchText] = useState("");
  const [earningModalVisible, setEarningModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [reshareModalVisible, setReshareModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [shareLink, setShareLink] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchLeadReport();
  }, []);

  const fetchLeadReport = async () => {
    setLoading(true);
    try {
      const result = await LeadService.leadReport(
        fromDate.format("YYYY-MM-DD"),
        toDate.format("YYYY-MM-DD"),
        searchText
      );

      if (!result.success) {
        return Swal.fire("Oops!", result.message || "Failed to fetch", "error");
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        setData(responseData.data.result || []);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Request Failed",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const renderStatusTag = (status) => {
    let color = "blue";
    let label = status;

    switch (status) {
      case "PENDING":
        color = "orange";
        label = "IN PROGRESS";
        break;
      case "SUCCESS":
        color = "green";
        label = "COMPLETED";
        break;
      case "REJECTED":
        color = "red";
        label = "REJECTED";
        break;
      default:
        color = "blue";
        label = status;
    }

    return <Tag color={color}>{label}</Tag>;
  };

  const handleFilter = () => {
    fetchLeadReport();
  };

  const showEarningModal = (product) => {
    setSelectedProduct(JSON.parse(product));
    setEarningModalVisible(true);
  };

  const showDetailsModal = (lead) => {
    setSelectedLead(lead);
    setDetailsModalVisible(true);
  };

  const showReshareModal = (lead) => {
    const link = lead.redirect_url; // Replace with real link
    setShareLink(link);
    setReshareModalVisible(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareLink).then(() => {
      Swal.fire("Copied!", "Link copied to clipboard", "success");
    });
  };

  return (
    <div className="position-relative">
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100"
          style={{ backgroundColor: "rgba(255,255,255,0.7)", zIndex: 9999 }}
        >
          <Spin size="large" tip="Loading..." />
        </div>
      )}

      <Header />

      <div className="bg-light py-5 px-3 d-flex align-items-center justify-content-center">
        <div
          className="card rounded-4 w-100"
          style={{ maxWidth: "1200px", minHeight: "800px" }}
        >
          <div className="card-body p-4">
            <Button
              type="default"
              className="mb-2"
              onClick={() => navigate(-1)}
              style={{ background: "#f0f0f0", borderRadius: "8px" }}
            >
              ← Back
            </Button>

            <div className="section-title mb-4">
              <span className="line"></span>
              <span className="text">MY LEADS</span>
              <span className="line"></span>
            </div>
            <form className="mb-3">
              <div className="row g-2 align-items-end">
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate ? fromDate.format("YYYY-MM-DD") : ""}
                    onChange={(e) =>
                      setFromDate(e.target.value ? dayjs(e.target.value) : null)
                    }
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate ? toDate.format("YYYY-MM-DD") : ""}
                    onChange={(e) =>
                      setToDate(e.target.value ? dayjs(e.target.value) : null)
                    }
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Mobile / Name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3 d-grid">
                  <button
                    type="button"
                    onClick={handleFilter}
                    className="btn btn-primary"
                  >
                    Filter
                  </button>
                </div>
              </div>
            </form>
            <Row gutter={[16, 16]}>
              {data.length === 0 ? (
                <Col span={24} className="text-center">
                  <Text type="secondary">No leads found for this period.</Text>
                </Col>
              ) : (
                data.map((lead, index) => {
                  const commercials = lead.commercials || {};
                  const product = lead.tbl_lead_product || {};
                  const createdDate = dayjs(lead.created_on);
                  const nextUpdate = createdDate
                    .add(7, "day")
                    .format("YYYY-MM-DD");

                  return (
                    <Col key={index} xs={24} sm={12} md={8}>
                      <Card
                        title={
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <img
                              src={product.logo}
                              alt="Product Logo"
                              style={{ height: 30 }}
                            />
                            <span>{product.name}</span>
                          </div>
                        }
                        bordered
                        className="rounded-3 shadow-sm"
                      >
                        <div>
                          <p>
                            <UserOutlined /> <Text strong>Name:</Text>{" "}
                            {lead.customer_name}
                          </p>
                          <p>
                            <PhoneOutlined /> <Text strong>Mobile:</Text>{" "}
                            {lead.mobile}
                          </p>
                          <p>
                            <CalendarOutlined />{" "}
                            <Text strong>Lead Created On:</Text>{" "}
                            {createdDate.format("YYYY-MM-DD HH:mm")}
                          </p>
                          {lead.status === "PENDING" && (
                            <p>
                              <CalendarOutlined />{" "}
                              <Text strong>Next Update:</Text> {nextUpdate}
                            </p>
                          )}
                          <p>
                            <InfoCircleOutlined /> <Text strong>Status:</Text>{" "}
                            {renderStatusTag(lead.status)}
                          </p>
                          <div
                            style={{
                              marginTop: 10,
                              display: "flex",
                              gap: 5,
                              flexWrap: "wrap",
                            }}
                          >
                            {[
                              {
                                label: "View Details",
                                type: "primary",
                                onClick: () => showDetailsModal(lead),
                              },
                              {
                                label: "Reshare",
                                type: "primary",
                                onClick: () => showReshareModal(lead),
                              },
                              {
                                label: "Earning Details",
                                type: "primary",
                                onClick: () => {
                                  //  alert(JSON.stringify(commercials, null, 2));
                                  showEarningModal(commercials);
                                },
                                style: {
                                  backgroundColor: "#52c41a",
                                  borderColor: "#52c41a",
                                },
                              },
                            ].map((btn, idx) => (
                              <Button
                                key={idx}
                                type={btn.type}
                                style={{
                                  ...btn.style,
                                  flex: "1 1 calc(33.33% - 5px)", // 3 per row on web
                                  maxWidth: "100%",
                                }}
                                className="responsive-button"
                                onClick={btn.onClick}
                              >
                                {btn.label}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </Card>
                    </Col>
                  );
                })
              )}
            </Row>
          </div>
        </div>
      </div>

      <Footer />

      <EarningDetailsDialog
        open={earningModalVisible}
        onClose={() => setEarningModalVisible(false)}
        product={selectedProduct}
      />

      <Modal
        title={
          "Lead Details: " + (selectedLead?.tbl_lead_product?.name || "N/A")
        }
        open={detailsModalVisible}
        footer={null}
        onCancel={() => setDetailsModalVisible(false)}
      >
        {selectedLead && (
          <>
            <p>
              <UserOutlined /> <Text strong>Name:</Text>{" "}
              {selectedLead.customer_name}
            </p>
            <p>
              <PhoneOutlined /> <Text strong>Mobile:</Text>{" "}
              {selectedLead.mobile}
            </p>
            <p>
              <MailOutlined /> <Text strong>Email:</Text> {selectedLead.email}
            </p>
            <p>
              <IdcardOutlined /> <Text strong>PAN:</Text>{" "}
              {selectedLead.pan_number}
            </p>
            <p>
              <EnvironmentOutlined /> <Text strong>Pincode:</Text>{" "}
              {selectedLead.pincode}
            </p>
            <p>
              <InfoCircleOutlined /> <Text strong>Status:</Text>{" "}
              {renderStatusTag(selectedLead.status)}
            </p>
            <p>
              <CalendarOutlined /> <Text strong>Lead Created On:</Text>{" "}
              {dayjs(selectedLead.created_on).format("YYYY-MM-DD HH:mm")}
            </p>
          </>
        )}
      </Modal>

      <Modal
        title="Share Lead Link"
        open={reshareModalVisible}
        footer={null}
        onCancel={() => setReshareModalVisible(false)}
      >
        <p>
          <Text strong>Link:</Text>{" "}
          <Input
            value={shareLink}
            readOnly
            addonAfter={
              <Button type="link" onClick={copyToClipboard}>
                Copy
              </Button>
            }
          />
        </p>
        <p style={{ marginTop: 10 }}>
          <Button
            type="primary"
            style={{ backgroundColor: "#25D366", borderColor: "#25D366" }}
            icon={<i className="fab fa-whatsapp"></i>}
            href={`https://wa.me/?text=${encodeURIComponent(shareLink)}`}
            target="_blank"
          >
            Share on WhatsApp
          </Button>
        </p>
      </Modal>
    </div>
  );
};

export default MyLeads;
